import React, { useContext } from 'react';
import { Box, Stack, Text, Checkbox, Link } from '@ltvco/refresh-lib/theme';
import { PLAN_DISPLAY } from '../../../constants';
import { AppConstants } from '@ltvco/refresh-lib/ctx';

interface TermsAndConditionsProps {
  terms1: boolean;
  terms2: boolean;
  onChangeTerms1: (checked: boolean) => void;
  onChangeTerms2: (checked: boolean) => void;
  formErrors?: { terms1?: string; terms2?: string } | any;
}

export const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  terms1,
  terms2,
  onChangeTerms1,
  onChangeTerms2,
  formErrors,
}) => {
  const {
    data: { supportPhone, supportEmail },
    links,
  } = useContext(AppConstants);

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Text variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Terms & Conditions
      </Text>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-start">
          <Checkbox
            id="terms1-checkbox"
            checked={terms1}
            onChange={(e) => onChangeTerms1(e.target.checked)}
            sx={{ mt: 0.5 }}
          />
          <Text
            variant="body2"
            component="label"
            htmlFor="terms1-checkbox"
            sx={{ cursor: 'pointer', userSelect: 'none' }}
          >
            You understand that the trial is limited to running{' '}
            {PLAN_DISPLAY.monthlyLimit} reports during the trial period, which
            lasts {PLAN_DISPLAY.trialPeriod} days. If you would like to cancel
            before your trial ends, you may do so for any reason. Simply call{' '}
            <Link href={`tel:${supportPhone}`} underline="always">
              {supportPhone}
            </Link>
            , email{' '}
            <Link href={`mailto:${supportEmail}`} underline="always">
              {supportEmail}
            </Link>{' '}
            or use our{' '}
            <Link
              href={links?.contactLink}
              underline="always"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Us form
            </Link>
            . Otherwise, your trial will end after {PLAN_DISPLAY.trialPeriod}{' '}
            days, at which time you will be charged the standard monthly rate of{' '}
            {PLAN_DISPLAY.recurringPrice} plus applicable sales tax. Your
            membership will automatically renew every month thereafter until you
            cancel, and your membership is limited to running{' '}
            {PLAN_DISPLAY.monthlyLimit} reports per month.
          </Text>
        </Stack>
        {formErrors?.terms1 && (
          <Text color="error" variant="caption" sx={{ pl: 4 }}>
            {formErrors.terms1}
          </Text>
        )}
        <Stack direction="row" alignItems="flex-start">
          <Checkbox
            id="terms2-checkbox"
            checked={terms2}
            onChange={(e) => onChangeTerms2(e.target.checked)}
            sx={{ mt: 0.5 }}
          />
          <Text
            variant="body2"
            component="label"
            htmlFor="terms2-checkbox"
            sx={{ cursor: 'pointer', userSelect: 'none' }}
          >
            By clicking the checkbox, you represent that you are over 18 years
            of age and agree to the{' '}
            <Link
              href={links?.tosLink}
              underline="always"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </Link>
            ,{' '}
            <Link
              href="https://www.bumper.com/nmvtis-disclaimer/"
              underline="always"
              target="_blank"
              rel="noopener noreferrer"
            >
              NMVTIS Disclaimer
            </Link>{' '}
            and{' '}
            <Link
              href={links?.privacyLink}
              underline="always"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Link>
            , and you agree to receive email from Bumper.com. You also
            understand that Bumper does not guarantee that it has information on
            any particular vehicle, and vehicle history reports are based solely
            on information supplied to Bumper.
          </Text>
        </Stack>
        {formErrors?.terms2 && (
          <Text color="error" variant="caption" sx={{ pl: 4 }}>
            {formErrors.terms2}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export default TermsAndConditions;
